import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  Hash,
  Loader2,
  RefreshCw,
  Search,
  ShieldCheck,
  Target,
  Trophy,
  Eye,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';

import { AppDispatch, RootState } from '../../slices';
import { fetchMarkets } from '../../slices/market/marketSlice';
import {
  clearError,
  clearLastDeclaration,
  clearSuccess,
  clearWinnerPreview,
  previewWinners,
  declareCloseResult,
  declareOpenResult,
  revertOpenResult,
  revertCloseResult,
  fetchResults,
  fetchSettlementRuns,
  MarketResult,
  ResultSession,
} from '../../slices/result/resultSlice';

type CallbackType = 'maharashtra_market' | 'delhi_market';

const today = () => {
  // Regular-market business dates are authoritative in Asia/Kolkata,
  // regardless of the admin user's browser/device timezone.
  //
  // Keep this input editable because a tightly controlled previous-day
  // CLOSE may legitimately be completed shortly after midnight IST.
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;

  if (!year || !month || !day) {
    throw new Error(
      'Unable to resolve the current Asia/Kolkata business date.'
    );
  }

  return `${year}-${month}-${day}`;
};

const calculateDigit = (pana: string) => {
  if (!/^\d{3}$/.test(pana)) return '';
  return String(
    pana.split('').reduce((sum, value) => sum + Number(value), 0) % 10
  );
};

const formatDate = (value: string) => {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
};

const DeclareResult = () => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    results,
    resultsCount,
    resultsTotalPages,
    resultsCurrentPage,
    resultsPageSize,
    resultsLoading,
    settlementRuns,
    declaring,
    revertingOpen,
    revertingClose,
    error,
    success,
    lastDeclaration,
    winnerPreview,
    previewingWinners,
  } = useSelector((state: RootState) => state.Result);

  const { markets, loading: marketsLoading } = useSelector(
    (state: RootState) => state.Market
  );

  const [marketId, setMarketId] = useState('');
  const [resultDate, setResultDate] = useState(today());
  const [session, setSession] = useState<ResultSession>('open');
  const [pana, setPana] = useState('');
  const [callbackType, setCallbackType] =
    useState<CallbackType>('maharashtra_market');
  const [search, setSearch] = useState('');
  const [resultsPage, setResultsPage] = useState(1);
  const [revertTarget, setRevertTarget] = useState<{
    result: MarketResult;
    session: ResultSession;
  } | null>(null);

  const [showWinnerModal, setShowWinnerModal] = useState(false);

  const digit = useMemo(() => calculateDigit(pana), [pana]);

  const selectedMarket = useMemo(
    () => markets.find((market) => String(market.id) === marketId),
    [markets, marketId]
  );

  const recentResults = results;

  const resultsFrom =
    resultsCount === 0
      ? 0
      : (resultsCurrentPage - 1) * resultsPageSize + 1;

  const resultsTo = Math.min(
    resultsCurrentPage * resultsPageSize,
    resultsCount
  );

  const visiblePages = useMemo(() => {
    const pages: number[] = [];

    const start = Math.max(
      1,
      Math.min(
        resultsCurrentPage - 2,
        Math.max(1, resultsTotalPages - 4)
      )
    );

    const end = Math.min(
      resultsTotalPages,
      start + 4
    );

    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    return pages;
  }, [resultsCurrentPage, resultsTotalPages]);

  const completedCount = results.filter(
    (result) => result.open_pana && result.close_pana
  ).length;

  const getResultSettlementStatus = (result: MarketResult) => {
    if (!result.market_id) return 'pending';

    const runs = settlementRuns.filter(
      (run) =>
        run.market_id === result.market_id &&
        run.settlement_date === result.result_date
    );

    const requiredSessions: ResultSession[] = [];

    if (result.open_pana) requiredSessions.push('open');
    if (result.close_pana) requiredSessions.push('close');

    if (requiredSessions.length === 0) return 'pending';

    const requiredRuns = requiredSessions.map((requiredSession) =>
      runs.find((run) => run.session === requiredSession)
    );

    if (requiredRuns.some((run) => run?.status === 'failed')) {
      return 'failed';
    }

    if (requiredRuns.some((run) => run?.status === 'running')) {
      return 'running';
    }

    if (
      requiredRuns.every(
        (run) => run && run.status === 'completed'
      )
    ) {
      return 'completed';
    }

    return 'pending';
  };

  const syncedCount = results.filter(
    (result) => getResultSettlementStatus(result) === 'completed'
  ).length;

  useEffect(() => {
    dispatch(fetchMarkets({ page: 1, page_size: 100 }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchSettlementRuns({
        settlement_date: resultDate,
        limit: 100,
      })
    );
  }, [dispatch, resultDate]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      dispatch(
        fetchResults({
          page: resultsPage,
          page_size: 10,
          result_date: resultDate,
          search: search.trim(),
        })
      );
    }, 250);

    return () => window.clearTimeout(timer);
  }, [dispatch, resultsPage, search, resultDate]);

  useEffect(() => {
    if (!success) return;
    const timer = window.setTimeout(() => dispatch(clearSuccess()), 5000);
    return () => window.clearTimeout(timer);
  }, [dispatch, success]);

  useEffect(() => {
    if (!error) return;
    const timer = window.setTimeout(() => dispatch(clearError()), 7000);
    return () => window.clearTimeout(timer);
  }, [dispatch, error]);

  const refresh = () => {
    dispatch(fetchMarkets({ page: 1, page_size: 100 }));
    dispatch(
      fetchSettlementRuns({
        settlement_date: resultDate,
        limit: 100,
      })
    );
    dispatch(
      fetchResults({
        page: resultsPage,
        page_size: 10,
        result_date: resultDate,
        search: search.trim(),
      })
    );
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();

    if (!marketId || !resultDate || !/^\d{3}$/.test(pana)) return;

    const payload = {
      market_id: Number(marketId),
      result_date: resultDate,
      pana,
      digit,
      callback_type: callbackType,
    };

    const action =
      session === 'open'
        ? declareOpenResult(payload)
        : declareCloseResult(payload);

    const response = await dispatch(action);

    if (
      declareOpenResult.fulfilled.match(response) ||
      declareCloseResult.fulfilled.match(response)
    ) {
      setPana('');
      setResultsPage(1);
      dispatch(
        fetchResults({
          page: 1,
          page_size: 10,
          result_date: resultDate,
          search: search.trim(),
        })
      );
    }
  };

  const openWinnerPreview = async () => {
    if (!marketId || !resultDate || !/^\d{3}$/.test(pana)) return;

    dispatch(clearWinnerPreview());

    const response = await dispatch(
      previewWinners({
        market_id: Number(marketId),
        result_date: resultDate,
        session,
        pana,
        digit,
        callback_type: callbackType,
        winner_limit: 100,
      })
    );

    if (previewWinners.fulfilled.match(response)) {
      setShowWinnerModal(true);
    }
  };

  const closeWinnerPreview = () => {
    setShowWinnerModal(false);
    dispatch(clearWinnerPreview());
  };

  const confirmRevert = async () => {
    if (!revertTarget?.result.market_id) return;

    const payload = {
      market_id: revertTarget.result.market_id,
      result_date: revertTarget.result.result_date,
      callback_type: revertTarget.result.callback_type as CallbackType,
      confirm: true as const,
    };

    const action =
      revertTarget.session === 'open'
        ? revertOpenResult(payload)
        : revertCloseResult(payload);

    const response = await dispatch(action);

    const succeeded =
      revertOpenResult.fulfilled.match(response) ||
      revertCloseResult.fulfilled.match(response);

    if (succeeded) {
      setRevertTarget(null);
      dispatch(
        fetchResults({
          page: resultsPage,
          page_size: 10,
          result_date: resultDate,
          search: search.trim(),
        })
      );
    }
  };

  const reverting = revertingOpen || revertingClose;

  const valid =
    Boolean(marketId) && Boolean(resultDate) && /^\d{3}$/.test(pana);

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="rounded-3xl bg-gradient-to-r from-slate-950 via-indigo-950 to-blue-900 p-6 text-white shadow-xl sm:p-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-sm">
                <ShieldCheck className="h-4 w-4" />
                Result Management
              </div>
              <h1 className="text-3xl font-bold sm:text-4xl">
                Declare Market Result
              </h1>
              <p className="mt-3 max-w-2xl text-sm text-slate-200 sm:text-base">
                Publish open and close results, verify the calculated digit,
                and review recent declarations.
              </p>
            </div>

            <button
              type="button"
              onClick={refresh}
              disabled={resultsLoading || marketsLoading}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-sm font-semibold hover:bg-white/20 disabled:opacity-50"
            >
              <RefreshCw
                className={`h-4 w-4 ${
                  resultsLoading || marketsLoading ? 'animate-spin' : ''
                }`}
              />
              Refresh
            </button>
          </div>
        </section>

        {(success || error) && (
          <div
            className={`flex gap-3 rounded-2xl border p-4 ${
              error
                ? 'border-red-200 bg-red-50 text-red-800'
                : 'border-emerald-200 bg-emerald-50 text-emerald-800'
            }`}
          >
            {error ? (
              <AlertCircle className="h-5 w-5 shrink-0" />
            ) : (
              <CheckCircle2 className="h-5 w-5 shrink-0" />
            )}
            <p className="text-sm font-medium">{error || success}</p>
          </div>
        )}

        <section className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Loaded results</p>
                <p className="mt-2 text-3xl font-bold">{results.length}</p>
              </div>
              <Target className="h-7 w-7 text-blue-600" />
            </div>
          </div>

          <div className="rounded-2xl border bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Completed</p>
                <p className="mt-2 text-3xl font-bold">{completedCount}</p>
              </div>
              <Trophy className="h-7 w-7 text-emerald-600" />
            </div>
          </div>

          <div className="rounded-2xl border bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Synced</p>
                <p className="mt-2 text-3xl font-bold">{syncedCount}</p>
              </div>
              <ShieldCheck className="h-7 w-7 text-violet-600" />
            </div>
          </div>
        </section>

        <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="rounded-3xl border bg-white p-5 shadow-sm sm:p-6">
            <h2 className="text-xl font-bold">Result declaration</h2>
            <p className="mt-1 text-sm text-slate-500">
              Select the market, session, date, and three-digit pana.
            </p>

            <form onSubmit={submit} className="mt-6 space-y-5">
              <div className="grid gap-5 md:grid-cols-2">
                <label>
                  <span className="mb-2 block text-sm font-semibold">
                    Market
                  </span>
                  <select
                    value={marketId}
                    onChange={(event) => setMarketId(event.target.value)}
                    disabled={marketsLoading}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  >
                    <option value="">
                      {marketsLoading ? 'Loading markets...' : 'Select market'}
                    </option>
                    {markets.map((market) => (
                      <option key={market.id} value={market.id}>
                        {market.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  <span className="mb-2 block text-sm font-semibold">
                    Result date
                  </span>
                  <div className="relative">
                    <CalendarDays className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                      type="date"
                      value={resultDate}
                      onChange={(event) => setResultDate(event.target.value)}
                      className="w-full rounded-xl border border-slate-300 py-3 pl-11 pr-4 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                    />
                  </div>
                </label>
              </div>

              <div>
                <span className="mb-2 block text-sm font-semibold">Session</span>
                <div className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1.5">
                  {(['open', 'close'] as ResultSession[]).map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setSession(value)}
                      className={`rounded-xl px-4 py-3 text-sm font-bold capitalize ${
                        session === value
                          ? value === 'open'
                            ? 'bg-emerald-600 text-white'
                            : 'bg-indigo-600 text-white'
                          : 'text-slate-600 hover:bg-white'
                      }`}
                    >
                      {value} result
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-5 md:grid-cols-[1fr_180px]">
                <label>
                  <span className="mb-2 block text-sm font-semibold">Pana</span>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                      value={pana}
                      onChange={(event) => {
                        setPana(
                          event.target.value.replace(/\D/g, '').slice(0, 3)
                        );
                        if (lastDeclaration) dispatch(clearLastDeclaration());
                      }}
                      inputMode="numeric"
                      placeholder="123"
                      className="w-full rounded-xl border border-slate-300 py-3 pl-11 pr-4 text-lg font-bold tracking-[0.3em] outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                    />
                  </div>
                </label>

                <div>
                  <span className="mb-2 block text-sm font-semibold">
                    Calculated digit
                  </span>
                  <div className="flex h-[50px] items-center justify-center rounded-xl border border-blue-200 bg-blue-50 text-2xl font-black text-blue-700">
                    {digit || '—'}
                  </div>
                </div>
              </div>

              <label>
                <span className="mb-2 block text-sm font-semibold">
                  Callback type
                </span>
                <select
                  value={callbackType}
                  onChange={(event) =>
                    setCallbackType(event.target.value as CallbackType)
                  }
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                >
                  <option value="maharashtra_market">
                    Maharashtra market
                  </option>
                  <option value="delhi_market">Delhi market</option>
                </select>
              </label>

              <div className="rounded-2xl bg-slate-50 p-4 text-sm">
                <p>
                  <span className="text-slate-500">Market:</span>{' '}
                  <strong>{selectedMarket?.name || 'Not selected'}</strong>
                </p>
                <p className="mt-2">
                  <span className="text-slate-500">Preview:</span>{' '}
                  <strong className="uppercase">
                    {session} · {pana || '---'}-{digit || '-'}
                  </strong>
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={openWinnerPreview}
                  disabled={!valid || previewingWinners || declaring}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-violet-300 bg-violet-50 px-5 py-3.5 font-bold text-violet-700 transition hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {previewingWinners ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <Eye className="h-5 w-5" />
                      Show Winner's
                    </>
                  )}
                </button>

                <button
                  type="submit"
                  disabled={!valid || declaring || previewingWinners}
                  className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3.5 font-bold text-white disabled:cursor-not-allowed disabled:opacity-50 ${
                    session === 'open'
                      ? 'bg-emerald-600 hover:bg-emerald-700'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                >
                  {declaring ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Declaring...
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="h-5 w-5" />
                      Declare {session} result
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          <aside className="rounded-3xl border bg-white p-5 shadow-sm sm:p-6">
            <h2 className="text-lg font-bold">Declaration status</h2>

            {lastDeclaration ? (
              <div className="mt-5 space-y-4">
                <div className="rounded-2xl bg-emerald-50 p-4 text-emerald-800">
                  <p className="font-bold">Declaration accepted</p>
                  <p className="mt-1 text-xs">
                    Settlement queued:{' '}
                    {lastDeclaration.settlement_queued ? 'Yes' : 'No'}
                  </p>
                </div>

                <div className="space-y-3 text-sm">
                  <p>
                    <span className="text-slate-500">Market:</span>{' '}
                    <strong>{lastDeclaration.result.market_name}</strong>
                  </p>
                  <p>
                    <span className="text-slate-500">Session:</span>{' '}
                    <strong className="capitalize">
                      {lastDeclaration.session}
                    </strong>
                  </p>
                  <p>
                    <span className="text-slate-500">Date:</span>{' '}
                    <strong>
                      {formatDate(lastDeclaration.result.result_date)}
                    </strong>
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => dispatch(clearLastDeclaration())}
                  className="w-full rounded-xl border px-4 py-2.5 text-sm font-semibold hover:bg-slate-50"
                >
                  Clear status
                </button>
              </div>
            ) : (
              <p className="mt-5 rounded-2xl bg-slate-50 p-5 text-sm text-slate-500">
                The latest successful declaration will appear here.
              </p>
            )}
          </aside>
        </section>

        <section className="overflow-hidden rounded-3xl border bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
            <div>
              <h2 className="text-xl font-bold">Recent results</h2>
              <p className="mt-1 text-sm text-slate-500">
                Showing {resultsFrom}-{resultsTo} of {resultsCount} matching records.
              </p>
            </div>

            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setResultsPage(1);
                }}
                placeholder="Search market, pana, digit, jodi..."
                className="w-full rounded-xl border py-2.5 pl-10 pr-4 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y">
              <thead className="bg-slate-50 text-left text-xs font-bold uppercase text-slate-500">
                <tr>
                  <th className="px-6 py-3">Market</th>
                  <th className="px-6 py-3">Date</th>
                  <th className="px-6 py-3">Open</th>
                  <th className="px-6 py-3">Close</th>
                  <th className="px-6 py-3">Jodi</th>
                  <th className="px-6 py-3">Sync</th>
                  <th className="px-6 py-3">Bid Revert</th>
                </tr>
              </thead>

              <tbody className="divide-y">
                {resultsLoading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <Loader2 className="mx-auto h-7 w-7 animate-spin text-blue-600" />
                    </td>
                  </tr>
                ) : recentResults.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-12 text-center text-sm text-slate-500"
                    >
                      No result records found.
                    </td>
                  </tr>
                ) : (
                  recentResults.map((result) => (
                    <tr key={result.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-semibold">
                        {result.market_name}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {formatDate(result.result_date)}
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-emerald-700">
                        {result.open_pana
                          ? `${result.open_pana}-${result.open_digit || '-'}`
                          : 'Pending'}
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-indigo-700">
                        {result.close_pana
                          ? `${result.close_pana}-${result.close_digit || '-'}`
                          : 'Pending'}
                      </td>
                      <td className="px-6 py-4 font-black">
                        {result.jodi_digit || '—'}
                      </td>
                      <td className="px-6 py-4">
                        {(() => {
                          const settlementStatus =
                            getResultSettlementStatus(result);

                          const statusClasses = {
                            completed:
                              'bg-emerald-100 text-emerald-700',
                            running:
                              'bg-blue-100 text-blue-700',
                            failed:
                              'bg-red-100 text-red-700',
                            pending:
                              'bg-amber-100 text-amber-700',
                          };

                          const statusLabels = {
                            completed: 'Completed',
                            running: 'Running',
                            failed: 'Failed',
                            pending: 'Pending',
                          };

                          return (
                            <span
                              className={`rounded-full px-2.5 py-1 text-xs font-bold ${statusClasses[settlementStatus]}`}
                            >
                              {statusLabels[settlementStatus]}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex min-w-[170px] flex-col gap-2">
                          {result.open_pana && (
                            <button
                              type="button"
                              disabled={!result.market_id || reverting}
                              onClick={() =>
                                setRevertTarget({
                                  result,
                                  session: 'open',
                                })
                              }
                              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-emerald-500 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <RefreshCw className="h-3.5 w-3.5" />
                              Open Bid Revert
                            </button>
                          )}

                          {result.close_pana && (
                            <button
                              type="button"
                              disabled={!result.market_id || reverting}
                              onClick={() =>
                                setRevertTarget({
                                  result,
                                  session: 'close',
                                })
                              }
                              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-red-500 bg-red-50 px-3 py-2 text-xs font-bold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <RefreshCw className="h-3.5 w-3.5" />
                              Close Bid Revert
                            </button>
                          )}

                          {!result.open_pana && !result.close_pana && (
                            <span className="text-xs font-semibold text-slate-400">
                              No action
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {resultsCount > 0 && (
            <div className="flex flex-col gap-3 border-t px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <p className="text-sm text-slate-500">
                Showing{' '}
                <span className="font-semibold text-slate-700">
                  {resultsFrom}-{resultsTo}
                </span>{' '}
                of{' '}
                <span className="font-semibold text-slate-700">
                  {resultsCount}
                </span>{' '}
                results
              </p>

              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  disabled={resultsCurrentPage <= 1 || resultsLoading}
                  onClick={() =>
                    setResultsPage((page) => Math.max(1, page - 1))
                  }
                  className="rounded-lg border px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Previous
                </button>

                {visiblePages[0] > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setResultsPage(1)}
                      className="h-9 min-w-9 rounded-lg border px-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      1
                    </button>

                    {visiblePages[0] > 2 && (
                      <span className="px-1 text-slate-400">…</span>
                    )}
                  </>
                )}

                {visiblePages.map((page) => (
                  <button
                    key={page}
                    type="button"
                    disabled={resultsLoading}
                    onClick={() => setResultsPage(page)}
                    className={`h-9 min-w-9 rounded-lg border px-3 text-sm font-bold transition ${
                      page === resultsCurrentPage
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}

                {visiblePages[visiblePages.length - 1] < resultsTotalPages && (
                  <>
                    {visiblePages[visiblePages.length - 1] <
                      resultsTotalPages - 1 && (
                      <span className="px-1 text-slate-400">…</span>
                    )}

                    <button
                      type="button"
                      onClick={() => setResultsPage(resultsTotalPages)}
                      className="h-9 min-w-9 rounded-lg border px-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      {resultsTotalPages}
                    </button>
                  </>
                )}

                <button
                  type="button"
                  disabled={
                    resultsCurrentPage >= resultsTotalPages ||
                    resultsLoading
                  }
                  onClick={() =>
                    setResultsPage((page) =>
                      Math.min(resultsTotalPages, page + 1)
                    )
                  }
                  className="rounded-lg border px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </section>
      </div>


        {showWinnerModal && winnerPreview && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeWinnerPreview();
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="winner-preview-title"
              className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-3xl bg-white shadow-2xl"
            >
              <div className="flex items-center justify-between bg-gradient-to-r from-violet-700 to-indigo-700 p-6 text-white">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-white/15 p-3">
                    <Trophy className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-white/70">
                      Winner preview
                    </p>
                    <h2 id="winner-preview-title" className="mt-1 text-xl font-black">
                      {winnerPreview.market_name}
                    </h2>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={closeWinnerPreview}
                  className="rounded-xl bg-white/10 px-4 py-2 text-sm font-bold hover:bg-white/20"
                >
                  Close
                </button>
              </div>

              <div className="max-h-[calc(90vh-100px)] overflow-y-auto p-6">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded-2xl bg-slate-50 p-4">
                    <p className="text-xs font-bold uppercase text-slate-400">Result</p>
                    <p className="mt-2 text-xl font-black uppercase text-slate-900">
                      {winnerPreview.session} · {winnerPreview.pana}-{winnerPreview.digit}
                    </p>
                  </div>

                  <div className="rounded-2xl bg-blue-50 p-4">
                    <p className="text-xs font-bold uppercase text-blue-500">Total bets</p>
                    <p className="mt-2 text-2xl font-black text-blue-800">
                      {winnerPreview.total_bets}
                    </p>
                  </div>

                  <div className="rounded-2xl bg-emerald-50 p-4">
                    <p className="text-xs font-bold uppercase text-emerald-500">
                      Winning bets
                    </p>
                    <p className="mt-2 text-2xl font-black text-emerald-800">
                      {winnerPreview.winning_bets}
                    </p>
                  </div>

                  <div className="rounded-2xl bg-violet-50 p-4">
                    <p className="text-xs font-bold uppercase text-violet-500">
                      Estimated payout
                    </p>
                    <p className="mt-2 text-2xl font-black text-violet-800">
                      ₹{winnerPreview.estimated_payout}
                    </p>
                  </div>
                </div>

                <div className="mt-6 overflow-hidden rounded-2xl border">
                  <div className="border-b bg-slate-50 px-5 py-4">
                    <h3 className="font-bold text-slate-900">
                      Winners ({winnerPreview.winner_count_returned})
                    </h3>
                  </div>

                  {winnerPreview.winners.length === 0 ? (
                    <div className="p-10 text-center text-sm text-slate-500">
                      No winners found for this result.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y">
                        <thead className="bg-slate-50 text-left text-xs font-bold uppercase text-slate-500">
                          <tr>
                            <th className="px-5 py-3">User</th>
                            <th className="px-5 py-3">Mobile</th>
                            <th className="px-5 py-3">Bet type</th>
                            <th className="px-5 py-3">Bet number</th>
                            <th className="px-5 py-3">Stake</th>
                            <th className="px-5 py-3">Estimated payout</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {winnerPreview.winners.map((winner) => (
                            <tr key={winner.bet_id} className="hover:bg-slate-50">
                              <td className="px-5 py-4 text-sm font-semibold">
                                {winner.user_name || `User #${winner.user_id || '-'}`}
                              </td>
                              <td className="px-5 py-4 text-sm">
                                {winner.mobile_number || '—'}
                              </td>
                              <td className="px-5 py-4 text-sm">
                                {winner.bet_type_name || '—'}
                              </td>
                              <td className="px-5 py-4 text-sm font-bold text-slate-900">
                                {winner.selected_number || '—'}
                              </td>
                              <td className="px-5 py-4 text-sm font-bold">
                                ₹{winner.amount}
                              </td>
                              <td className="px-5 py-4 text-sm font-black text-emerald-700">
                                ₹{winner.estimated_payout}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {winnerPreview.winners_truncated && (
                    <div className="border-t bg-amber-50 px-5 py-3 text-sm font-medium text-amber-800">
                      Only the first {winnerPreview.winner_limit} winners are displayed.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {revertTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="revert-dialog-title"
              className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl"
            >
              <div
                className={`p-6 text-white ${
                  revertTarget.session === 'open'
                    ? 'bg-emerald-700'
                    : 'bg-red-700'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-white/15 p-3">
                    <RefreshCw className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-white/70">
                      Confirm action
                    </p>
                    <h2
                      id="revert-dialog-title"
                      className="mt-1 text-xl font-black"
                    >
                      {revertTarget.session === 'open'
                        ? 'Open Bid Revert'
                        : 'Close Bid Revert'}
                    </h2>
                  </div>
                </div>
              </div>

              <div className="space-y-5 p-6">
                <div className="grid grid-cols-2 gap-3 rounded-2xl bg-slate-50 p-4 text-sm">
                  <div>
                    <p className="text-xs font-semibold uppercase text-slate-400">
                      Market
                    </p>
                    <p className="mt-1 font-bold text-slate-900">
                      {revertTarget.result.market_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-slate-400">
                      Date
                    </p>
                    <p className="mt-1 font-bold text-slate-900">
                      {formatDate(revertTarget.result.result_date)}
                    </p>
                  </div>
                </div>

                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                    <div>
                      <p className="font-bold">This operation is sensitive.</p>
                      <p className="mt-1 leading-6">
                        It will reverse settlement wallet entries, restore
                        affected bets to their pending state, and remove the
                        selected {revertTarget.session} result.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    disabled={reverting}
                    onClick={() => setRevertTarget(null)}
                    className="rounded-xl border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={reverting}
                    onClick={confirmRevert}
                    className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50 ${
                      revertTarget.session === 'open'
                        ? 'bg-emerald-700 hover:bg-emerald-800'
                        : 'bg-red-700 hover:bg-red-800'
                    }`}
                  >
                    {reverting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Reverting...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        Confirm Revert
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default DeclareResult;
